// v2 TV profiles.
//
// The waiting-room display is configured in the DASHBOARD (Settings -> TV
// Configuration), not on the TV. Staff create named profiles there — which
// doctors to show, which fields, which layout — and the TV's only job is to
// pick one. This module fetches those profiles and normalises them into the
// shape the v2 screens consume, so no v2 component has to know about the v1
// field names or the legacy view_type enum.
import {
  getProfilesByHospitalId,
  getProfileById,
} from "../../lib/apis/profile";
import { DEFAULT_TV_CONFIG, TVDisplayConfig, toBool, toLimit } from "./tvConfig";
import { ViewMode } from "../utils/selection";

export interface ProfileDoctor {
  doctor_id?: string;
  mapping_id: string;
  full_name?: string;
  specialization?: string;
}

export interface TVProfile {
  profileId: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  viewMode: ViewMode;
  language: string;
  fields: TVDisplayConfig;
  doctors: ProfileDoctor[];
}

// v1 stores ('column' | 'carousel' | 'single-doctor'), and leaves it NULL on
// default profiles. v2 has two layouts, and 'single-doctor' is precisely what
// we call slideshow — so anything that isn't an explicit 'column' becomes
// slideshow, with NULL falling back to column.
const toViewMode = (raw: any): ViewMode =>
  raw === "carousel" || raw === "single-doctor" || raw === "slideshow"
    ? "slideshow"
    : "column";

const normalise = (raw: any): TVProfile | null => {
  const profileId = raw?.tv_config_profile_id ?? raw?.id ?? raw?.profile_id;
  if (!profileId) return null;
  return {
    profileId,
    name: raw.tv_profile_name ?? raw.profile_name ?? "Untitled profile",
    isDefault: raw.is_default === true,
    isActive: raw.is_active !== false,
    viewMode: toViewMode(raw.view_type),
    language: raw.language ?? "en",
    fields: {
      token: toBool(raw.token, DEFAULT_TV_CONFIG.token),
      patient_name: toBool(raw.patient_name, DEFAULT_TV_CONFIG.patient_name),
      room_number: toBool(raw.room_number, DEFAULT_TV_CONFIG.room_number),
      slot: toBool(raw.slot, DEFAULT_TV_CONFIG.slot),
      booking_mode: toBool(raw.booking_mode, DEFAULT_TV_CONFIG.booking_mode),
      patient_display_limit: toLimit(raw.patient_display_limit),
    },
    // The API resolves each doctor to its hospital_doctor_mapping row, which
    // is the id the entire v2 queue is keyed by. A doctor without one is no
    // longer mapped to this hospital and can't be rendered, so drop it here
    // rather than letting it become an empty card later.
    doctors: (Array.isArray(raw.doctors_list) ? raw.doctors_list : [])
      .filter((d: any) => d && d.mapping_id)
      .map((d: any) => ({
        doctor_id: d.doctor_id,
        mapping_id: d.mapping_id,
        full_name: d.full_name,
        specialization: d.specialization,
      })),
  };
};

const isOk = (res: any): boolean =>
  res?.status === 200 &&
  (res?.data?.status === "SUCCESS" || res?.data?.status === 1);

// All active profiles for the hospital, for the picker screen.
export const fetchProfiles = async (
  hospitalId: string
): Promise<TVProfile[]> => {
  const res = await getProfilesByHospitalId(hospitalId);
  if (!isOk(res)) {
    throw new Error(res?.data?.error || "Could not load TV profiles.");
  }
  const rows = Array.isArray(res.data.result) ? res.data.result : [];
  return rows
    .map(normalise)
    .filter((p: TVProfile | null): p is TVProfile => p !== null);
};

// One profile by id, re-read by the display on every load so a dashboard edit
// reaches the screen without anyone touching the TV. Returns null if it was
// deleted, so the caller can send the TV back to the picker.
export const fetchProfile = async (
  profileId: string
): Promise<TVProfile | null> => {
  const res = await getProfileById(profileId);
  if (!isOk(res)) return null;
  return normalise(res.data.result ?? res.data.data);
};
